import { concat } from '@ember/helper';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { computed } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import HotelMarketInsightCardsCardBreakdown from 'lumen/components/hotel/market-insight/cards/card-breakdown';
import HotelMarketInsightChartsChartSearchesOverview from 'lumen/components/hotel/market-insight/charts/chart-searches-overview';
import HotelMarketInsightListsListCountriesSearching from 'lumen/components/hotel/market-insight/lists/list-countries-searching';
import HotelMarketInsightListsListLos from 'lumen/components/hotel/market-insight/lists/list-los';
import type { Day } from 'lumen/models';
import {
  MI_SEARCHES_TYPE_FLIGHTS_BUSINESS,
  MI_SEARCHES_TYPE_FLIGHTS_LEISURE,
  MI_SEARCHES_TYPE_HOTELS_BUSINESS,
  MI_SEARCHES_TYPE_HOTELS_LEISURE,
} from 'lumen/utils/constants/general-constants';
import { FIXED_EXTRACT_DATE_LATEST } from 'lumen/utils/constants/market-insight-constants';

import styles from './tab-searches.module.css';

interface HotelMarketInsightTabsTabSearchesSignature {
  Args: {
    params: unknown;
  };
}

export default class HotelMarketInsightTabsTabSearches extends Component<HotelMarketInsightTabsTabSearchesSignature> {
  @service declare miSearches: Services['mi-searches'];
  @service declare miSelections: Services['mi-selections'];
  @service declare store: Services['store'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get countrySearches() {
    let searchType = MI_SEARCHES_TYPE_FLIGHTS_LEISURE;
    if (this.miSelections.isHotelSearchesTabActive) {
      searchType = MI_SEARCHES_TYPE_HOTELS_LEISURE;
    } else if (this.miSelections.isFlightBusinessSearchesTabActive) {
      searchType = MI_SEARCHES_TYPE_FLIGHTS_BUSINESS;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.getCountrySearchesDataForOverview(searchType);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get endDateString() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.overviewRange.end.format('YYYY-MM-DD');
  }

  @computed('args.params.isLoading')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLoading() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.params?.isLoading;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get losSearches() {
    let searchType = MI_SEARCHES_TYPE_FLIGHTS_LEISURE;
    if (this.miSelections.isHotelSearchesTabActive) {
      searchType = MI_SEARCHES_TYPE_HOTELS_LEISURE;
    } else if (this.miSelections.isHotelBusinessSearchesTabActive) {
      searchType = MI_SEARCHES_TYPE_HOTELS_BUSINESS;
    } else if (this.miSelections.isFlightBusinessSearchesTabActive) {
      searchType = MI_SEARCHES_TYPE_FLIGHTS_BUSINESS;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.getLosSearchesDataForOverview(searchType);
  }

  @computed('args.params.overviewRange')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get overviewRange() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.params?.overviewRange;
  }

  // Although the overview is over a range, the breakdown values are still linked to a single day. This is that day
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get startOfOverviewRangeDayModel() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.overviewRange.start && !this.isLoading) {
      return this.store.peekRecord<Day>(
        'day',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        this.overviewRange.start.format('YYYY-MM-DD'),
      );
    }

    return null;
  }

  // We get the searches for the correct type, latest extract date, the above day model, and until the last day of the range
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getCountrySearchesDataForOverview(searchType) {
    if (!this.isLoading) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
      return this.miSearches.filterCountrySearchesByExtractDate(
        searchType,
        this.startOfOverviewRangeDayModel,
        FIXED_EXTRACT_DATE_LATEST,
        this.endDateString,
      );
    }

    return [];
  }

  // Next two functions are used for the breakdowns
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getLosSearchesDataForOverview(searchType) {
    if (!this.isLoading) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
      return this.miSearches.filterLosSearchesByExtractDate(
        searchType,
        this.startOfOverviewRangeDayModel,
        FIXED_EXTRACT_DATE_LATEST,
        this.endDateString,
      );
    }

    return [];
  }

  <template>
    <div class={{styles.container}}>
      <div class="mb-400">
        <HotelMarketInsightCardsCardBreakdown
          @title={{t "market_insight.search_patterns.search_level"}}
        >
          <HotelMarketInsightChartsChartSearchesOverview
            {{! @glint-expect-error: Incorrect type }}
            @onChartClicked={{@params.onChartClicked}}
            {{! @glint-expect-error: Incorrect type }}
            @onUpdateChartReference={{@params.onUpdateChartReference}}
            {{! @glint-expect-error: Incorrect type }}
            @searchsummaries={{@params.searchsummaries}}
          />
        </HotelMarketInsightCardsCardBreakdown>
      </div>

      <div class="flex flex-row">
        {{#unless this.miSelections.isHotelBusinessSearchesTabActive}}
          <div class={{concat styles.market-insight-breakdown " " "mr-300"}}>
            <HotelMarketInsightCardsCardBreakdown
              @title={{t "market_insight.top_searching_countries"}}
            >
              <HotelMarketInsightListsListCountriesSearching
                @activeSearchesType={{this.miSelections.searchesTab}}
                @countrySearches={{this.countrySearches}}
                @hasEmptyState={{true}}
                {{! @glint-expect-error: Incorrect type }}
                @isLoading={{@params.isLoading}}
                @listCssClasses="country-searches-list--limited-height"
                @wheelPropagation={{false}}
              />
            </HotelMarketInsightCardsCardBreakdown>
          </div>
        {{/unless}}

        <div class={{styles.market-insight-breakdown}}>
          <HotelMarketInsightCardsCardBreakdown
            @title={{t "revenue_insight.los"}}
          >
            <HotelMarketInsightListsListLos
              @hasEmptyState={{true}}
              {{! @glint-expect-error: Incorrect type }}
              @isLoading={{@params.isLoading}}
              @losSearches={{this.losSearches}}
            />
          </HotelMarketInsightCardsCardBreakdown>
        </div>
      </div>
    </div>
  </template>
}
